"use client";

import Link from "next/link";
import { ArrowRight, Rocket, Globe, Telescope, Quote } from "lucide-react";
import { Section, Container } from "@/components/ui/container";
import { Reveal } from "@/components/ui/reveal";
import { future as data } from "@/lib/data/about";

const expansionAccents = [
  "var(--color-cyan)",
  "var(--color-purple)",
  "var(--color-amber)",
] as const;
const expansionIcons = [Globe, Telescope, Rocket] as const;
const phaseAccents = [
  "var(--color-crimson)",
  "var(--color-purple)",
  "var(--color-cyan)",
] as const;

export function Future() {
  return (
    <Section id="future" className="relative">
      <div className="pointer-events-none absolute inset-0 -z-10">
        <div className="absolute top-1/4 right-1/3 h-96 w-96 rounded-full bg-[var(--color-cyan)] opacity-15 blur-[140px]" />
      </div>

      <Container>
        <Reveal>
          <div className="max-w-3xl">
            <span className="eyebrow-tiny text-[var(--color-cyan)]">{data.eyebrow}</span>
            <h2 className="mt-3">{data.title}</h2>
            <p className="mt-5 text-body text-[rgb(var(--fg-muted))] leading-relaxed">{data.intro}</p>
          </div>
        </Reveal>

        <div className="mt-12 grid sm:grid-cols-3 gap-5">
          {data.roadmap.map((r, i) => {
            const accent = phaseAccents[i % phaseAccents.length]!;
            return (
              <Reveal key={r.phase} delay={i * 100}>
                <div
                  className="glass rounded-2xl p-6 h-full"
                  style={{
                    boxShadow: `inset 0 0 0 1px color-mix(in oklch, ${accent} 12%, transparent)`,
                  }}
                >
                  <div className="eyebrow-tiny font-bold" style={{ color: accent }}>
                    {r.phase}
                  </div>
                  <h3 className="mt-2 text-lg">{r.title}</h3>
                  <ul className="mt-4 space-y-2">
                    {r.items.map((item) => (
                      <li key={item} className="flex items-start gap-2 text-xs text-[rgb(var(--fg-muted))] leading-relaxed">
                        <span
                          className="mt-1.5 h-1 w-1 rounded-full shrink-0"
                          style={{ background: accent }}
                        />
                        {item}
                      </li>
                    ))}
                  </ul>
                </div>
              </Reveal>
            );
          })}
        </div>

        <Reveal delay={400}>
          <div className="mt-10 glass rounded-2xl p-6">
            <div className="eyebrow-tiny text-[rgb(var(--fg-subtle))]">Market Expansion</div>
            <div className="mt-5 grid sm:grid-cols-3 gap-5">
              {data.marketExpansion.map((m, i) => {
                const Icon = expansionIcons[i % expansionIcons.length]!;
                const accent = expansionAccents[i % expansionAccents.length]!;
                return (
                  <div key={m.heading} className="flex items-start gap-3">
                    <span
                      className="grid place-items-center h-9 w-9 rounded-lg shrink-0"
                      style={{
                        background: `color-mix(in oklch, ${accent} 18%, transparent)`,
                        boxShadow: `inset 0 0 0 1px color-mix(in oklch, ${accent} 35%, transparent)`,
                      }}
                    >
                      <Icon className="h-4 w-4" style={{ color: accent }} />
                    </span>
                    <div>
                      <div className="text-sm font-bold">{m.heading}</div>
                      <div className="text-xs text-[rgb(var(--fg-muted))] mt-1 leading-relaxed">{m.body}</div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </Reveal>

        <Reveal delay={500}>
          <div className="mt-10 glass rounded-2xl p-7 relative overflow-hidden">
            <Quote
              className="absolute -top-2 -left-2 h-20 w-20 opacity-10"
              style={{ color: "var(--color-crimson)" }}
            />
            <div className="relative">
              <div className="eyebrow-tiny text-[var(--color-crimson)]">Long-Term Vision</div>
              <blockquote className="mt-3 text-xl sm:text-2xl font-bold leading-snug text-balance">
                &ldquo;{data.longTermVision.quote}&rdquo;
              </blockquote>
              <p className="mt-4 text-sm text-[rgb(var(--fg-muted))] leading-relaxed max-w-3xl">
                {data.longTermVision.body}
              </p>
            </div>
          </div>
        </Reveal>

        <Reveal delay={600}>
          <div className="mt-10 flex flex-wrap items-center gap-3">
            {data.cta.map((c, i) => (
              <Link
                key={c.label}
                href={c.href}
                className={
                  i === 0
                    ? "btn-primary-gradient h-11 px-6 inline-flex items-center gap-2 rounded-full font-semibold text-sm"
                    : "btn-glass h-11 px-6 inline-flex items-center gap-2 rounded-full font-semibold text-sm"
                }
              >
                {c.label} <ArrowRight className="h-3.5 w-3.5" />
              </Link>
            ))}
          </div>
        </Reveal>
      </Container>
    </Section>
  );
}
